import React, {
  ForwardRefRenderFunction,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Button, Drawer, Tag } from 'antd';
import { Expense, ExpenseStatus } from '../../dto/Expense';
import { useBusinessContext } from '../../context/BusinessContext';
import { useConnection, useUser } from '../../connection/connection';
import dayjs from 'dayjs';
import { title } from 'radash';
import { useEvents } from '../../common/EventContext';
import { Utils } from '../../common/Utils';
import { Permission } from '../../dto';

export type ExpenseViewModalInstance = {
  open: (expense: Expense) => void;
};

type Props = {};

const DetailView = ({
  label,
  value,
}: {
  label: string;
  value: string | React.ReactNode;
}) => {
  return (
    <div className="flex flex-col mb-4">
      <div className="text-gray-500 text-sm">{label}</div>
      <div className="text-base font-medium text-black">{value}</div>
    </div>
  );
};

const DetailRow = ({ children }: { children: React.ReactNode }) => {
  return <div className="grid grid-cols-2 gap-4 mb-4">{children}</div>;
};

export const ExpenseViewModal: ForwardRefRenderFunction<
  ExpenseViewModalInstance,
  Props
> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [expense, setExpense] = useState<Expense>();
  const business = useBusinessContext();
  const connection = useConnection();
  const events = useEvents();
  const userInfo = useUser();

  useImperativeHandle(
    ref,
    () => ({
      open: (expense: Expense) => {
        setExpense(expense);
        setVisible(true);
      },
    }),
    [],
  );

  const onStatusChange = (value: string) => {
    setSaving(true);
    const data = { ...expense, status: value };
    delete data.businessId;
    delete data.expenseId;
    delete data.createdAt;
    connection
      .patch(`expenses/${business.businessId}/${expense?.expenseId}`, data)
      .then((res) => {
        events.emit('EXPENSE', { action: 'UPDATE', data: res.data });
        setVisible(false);
      })
      .catch((error) => console.log(error))
      .finally(() => setSaving(false));
  };

  return (
    <Drawer
      title={'View Expense'}
      width={600}
      open={visible}
      footer={
        <div className="flex justify-end gap-2">
          {expense && expense.status === ExpenseStatus.REVIEWED && (
            <Button
              loading={saving}
              type={'primary'}
              danger
              disabled={
                !userInfo?.permissions?.includes(Permission.EXPENSE_APPROVE)
              }
              onClick={() => onStatusChange(ExpenseStatus.OPEN)}
            >
              Reject
            </Button>
          )}
          {expense && expense.status === ExpenseStatus.REVIEWED && (
            <Button
              loading={saving}
              type="primary"
              disabled={
                !userInfo?.permissions?.includes(Permission.EXPENSE_APPROVE)
              }
              onClick={() => onStatusChange(ExpenseStatus.APPROVED)}
            >
              Approve
            </Button>
          )}
          {expense && expense.status === ExpenseStatus.OPEN && (
            <Button
              loading={saving}
              type="primary"
              disabled={
                !userInfo?.permissions?.includes(Permission.EXPENSE_REVIEW)
              }
              onClick={() => onStatusChange(ExpenseStatus.REVIEWED)}
            >
              Review
            </Button>
          )}
          {expense && expense.status === ExpenseStatus.APPROVED && (
            <Button
              loading={saving}
              type="primary"
              disabled={
                !userInfo?.permissions?.includes(Permission.EXPENSE_APPROVE)
              }
              onClick={() => onStatusChange(ExpenseStatus.DONE)}
            >
              Done
            </Button>
          )}
        </div>
      }
      onClose={() => {
        setVisible(false);
        setExpense(undefined);
      }}
    >
      {expense && (
        <div className="p-4">
          <DetailRow>
            <DetailView
              label="Type"
              value={business.expenseTypes[expense.type].value}
            />
            <DetailView
              label="Amount"
              value={Utils.formatPrice(expense.amount)}
            />
          </DetailRow>
          <DetailRow>
            <DetailView
              label="Site"
              value={business.sites[expense.site].name}
            />
            <DetailView label="Date" value={expense.date} />
          </DetailRow>
          <DetailRow>
            <DetailView
              label="Created At"
              value={dayjs(expense.createdAt).format('YYYY-MM-DD HH:mm')}
            />
            <DetailView
              label="Status"
              value={
                <Tag
                  color={
                    expense.status === ExpenseStatus.OPEN
                      ? 'processing'
                      : expense.status === ExpenseStatus.REVIEWED
                        ? 'orange'
                        : expense.status === ExpenseStatus.APPROVED
                          ? 'lime'
                          : 'success'
                  }
                >
                  {expense.status}
                </Tag>
              }
            />
          </DetailRow>
          {expense.account && (
            <DetailRow>
              <DetailView
                label="Account"
                value={business.accounts[expense.account]?.name}
              />
            </DetailRow>
          )}
          <DetailRow>
            <DetailView label="VAT Type" value={expense.vatType} />
            <DetailView
              label="VAT Ammount"
              value={
                expense.vatAmount ? Utils.formatPrice(expense.vatAmount) : 'N/A'
              }
            />
          </DetailRow>
          <DetailRow>
            <DetailView label="Ref Number" value={expense?.refNo ?? 'N/A'} />
            <DetailView
              label="Cheque Number"
              value={expense?.chequeNo ?? 'N/A'}
            />
          </DetailRow>
          <DetailRow>
            <DetailView label="Payee" value={expense?.payee ?? 'N/A'} />
            <DetailView label="Voucher" value={expense?.voucher ?? 'N/A'} />
          </DetailRow>
          <DetailRow>
            <DetailView
              label="Created By"
              value={title(business.users[expense.createdBy].name)}
            />
            <DetailView
              label="Reviewed By"
              value={
                expense.reviewedBy
                  ? title(business.users[expense.reviewedBy].name)
                  : 'N/A'
              }
            />
          </DetailRow>
          <DetailRow>
            <DetailView
              label="Approved By"
              value={
                expense.approvedBy
                  ? title(business.users[expense.approvedBy].name)
                  : 'N/A'
              }
            />
            <DetailView
              label="Done By"
              value={
                expense.doneBy
                  ? title(business.users[expense.doneBy].name)
                  : 'N/A'
              }
            />
          </DetailRow>
          <div className="mb-4">
            <div className="text-gray-500 text-sm">Description</div>
            <div className="text-base font-medium text-black whitespace-pre-line">
              {expense.description}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export const useExpenseViewModal = () => useRef<ExpenseViewModalInstance>(null);

export default React.forwardRef<ExpenseViewModalInstance, Props>(
  ExpenseViewModal,
);
